import React, { useState } from 'react'
import {
    Avatar,
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import EventIcon from '@material-ui/icons/Event'
import RoomIcon from '@material-ui/icons/Room'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import StopIcon from '@material-ui/icons/Stop'
import { Container } from '../../components'
import { vendorProjectApi } from '../../contexts'
import {
    VENDOR_TASK_STATUS_COLORS,
    VENDOR_TASK_STATUS_LABELS,
    VendorProject,
    VendorTask,
    safeDateOnly,
} from '../../models'
import { useUser } from '../../hooks'
import { typographyStyle } from '../../theme/typographyStyle'
import { TaskTransitionButtons } from './TaskTransitionButtons'
import { VendorTaskForm } from './VendorTaskForm'

interface Props {
    task: VendorTask
    project: VendorProject
    onOpenMessages?: (task: VendorTask) => void
}

const formatTime = (s?: string | null): string => {
    if (!s) return ''
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return safeDateOnly(s) ?? ''
    return d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

const formatDate = (s?: string | null): string => {
    if (!s) return ''
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return safeDateOnly(s) ?? ''
    return d.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const initialsOf = (name?: string | null): string => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
}

export const TaskCard = (props: Props) => {
    const { task, project, onOpenMessages } = props
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const { deleteProjectTask } = vendorProjectApi()

    const [editOpen, setEditOpen] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)

    const colors = VENDOR_TASK_STATUS_COLORS[task.status]
    const showCreatedBy =
        !!task.assigned_by && task.assigned_by.id !== workspaceUser?.id

    const address =
        task.address && task.address !== 'No address' ? task.address : null

    return (
        <Paper
            elevation={0}
            variant="outlined"
            style={{
                marginTop: theme.spacing(1),
                padding: theme.spacing(1.5, 2),
                borderLeft: `4px solid ${colors.fg}`,
                borderRadius: 8,
                backgroundColor: 'white',
            }}
        >
            <Container alignItems="flex-start">
                <Container direction="column" flex={1} style={{ minWidth: 0 }}>
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1), flexWrap: 'wrap' }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 700,
                            }}
                        >
                            {task.title}
                        </Container>
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                padding: theme.spacing(0.25, 1),
                                backgroundColor: colors.bg,
                                color: colors.text,
                                borderRadius: 999,
                                ...typographyStyle(theme.typography.caption),
                                fontWeight: 700,
                                gap: 4,
                            }}
                        >
                            {task.status === 'IN_PROGRESS' && (
                                <PlayArrowIcon style={{ fontSize: 12 }} />
                            )}
                            {task.status === 'ON_HOLD' && (
                                <StopIcon style={{ fontSize: 12 }} />
                            )}
                            {VENDOR_TASK_STATUS_LABELS[task.status]}
                        </Container>
                    </Container>

                    <Container
                        alignItems="center"
                        style={{
                            marginTop: theme.spacing(0.75),
                            color: theme.palette.text.secondary,
                            ...typographyStyle(theme.typography.caption),
                            flexWrap: 'wrap',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        {task.assigned_to && (
                            <Container alignItems="center" style={{ gap: 6 }}>
                                <Avatar
                                    style={{
                                        width: 22,
                                        height: 22,
                                        backgroundColor:
                                            theme.palette.brand1.main,
                                        color: 'white',
                                        fontSize: 10,
                                        fontWeight: 700,
                                    }}
                                >
                                    {initialsOf(task.assigned_to.name_field)}
                                </Avatar>
                                {task.assigned_to.name_field ?? 'Unassigned'}
                            </Container>
                        )}

                        {task.schedule_time && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <EventIcon
                                    style={{ fontSize: 14, opacity: 0.7 }}
                                />
                                Scheduled {formatDate(task.schedule_time)}
                            </Container>
                        )}
                        {task.start_time && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <PlayArrowIcon
                                    style={{ fontSize: 14, opacity: 0.7 }}
                                />
                                Started {formatTime(task.start_time)}
                            </Container>
                        )}
                        {task.end_time && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <StopIcon
                                    style={{ fontSize: 14, opacity: 0.7 }}
                                />
                                Ended {formatTime(task.end_time)}
                            </Container>
                        )}

                        {address && (
                            <Container alignItems="center" style={{ gap: 4 }}>
                                <RoomIcon
                                    style={{ fontSize: 14, opacity: 0.7 }}
                                />
                                {address}
                            </Container>
                        )}
                    </Container>

                    {showCreatedBy && (
                        <Container
                            style={{
                                marginTop: theme.spacing(0.5),
                                color: theme.palette.text.secondary,
                                ...typographyStyle(theme.typography.caption),
                                fontStyle: 'italic',
                            }}
                        >
                            Created by{' '}
                            {task.assigned_by.name_field ?? 'Unknown'}
                        </Container>
                    )}

                    {task.description && (
                        <Container
                            style={{
                                marginTop: theme.spacing(1),
                                padding: theme.spacing(1, 1.25),
                                backgroundColor: '#f7f8f9',
                                borderRadius: 6,
                                ...typographyStyle(theme.typography.body2),
                                whiteSpace: 'pre-wrap',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {task.description}
                        </Container>
                    )}

                    <Container style={{ marginTop: theme.spacing(1.25) }}>
                        <TaskTransitionButtons task={task} />
                    </Container>
                </Container>

                <Container alignItems="center">
                    <Tooltip
                        title={
                            task.channel
                                ? 'Open messages'
                                : 'No chat channel for this task yet'
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={!task.channel || !onOpenMessages}
                                onClick={() => {
                                    if (task.channel && onOpenMessages) {
                                        onOpenMessages(task)
                                    }
                                }}
                            >
                                <Badge
                                    badgeContent={task.message_count ?? 0}
                                    color="secondary"
                                    overlap="rectangle"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    invisible={
                                        !task.message_count ||
                                        task.message_count <= 0
                                    }
                                    style={{ marginRight: 4, marginTop: 2 }}
                                >
                                    <ChatBubbleOutlineIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Edit task">
                        <IconButton
                            size="small"
                            onClick={() => setEditOpen(true)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete task">
                        <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(true)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Container>
            </Container>

            <VendorTaskForm
                open={editOpen}
                project={project}
                task={task}
                onClose={() => setEditOpen(false)}
            />

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
            >
                <DialogTitle>Delete Task</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete &quot;{task.title}
                        &quot;? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmDelete(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={async () => {
                            await deleteProjectTask(task.id)
                            setConfirmDelete(false)
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
